<template>
  <div class="Page Marker">
    <canvas class="Marker__spritesheet_canvas" ref="spritesheet_canvas" />
    <canvas class="Marker__canvas" ref="canvas" />
    <keep-alive :include="['Trone', 'Mrfleur', 'Card', 'Crown']">
      <Comp v-if="ready && phaserReady" :found="found" ref="marker" :is="$route.params.marker" />
    </keep-alive>
    <Capture :found="isFound" />
  </div>
</template>

<script>
import Page from '@/core/mixins/Page'
import { WebGLRenderer, PointLight, CanvasTexture, LinearFilter, Clock, Color, Scene, Camera, Group, AmbientLight, VideoTexture, DoubleSide, PlaneGeometry, MeshBasicMaterial, Mesh } from 'three'
import Capture from '@/components/capture/Capture'

import Singer from './markers/singer/Singer'
import Card from './markers/card/Card'
import Mrfleur from './markers/mrfleur/MrFleur'
import Trone from './markers/trone/Trone'
import Crown from './markers/crown/Crown'
// import Crown from './markers/crown/Crown3D'

export default {
  name: 'MarkerAr',
  mixins: [Page],
  beforeDestroy() {
    this.$renderer.dispose()
    this.$source.dispose()
    this.$controller.dispose()
    cancelAnimationFrame(this.raf)
  },
  components: { Singer, Card, Mrfleur, Trone, Crown, Capture },
  data: () => ({
    raf: null,
    ready: false,
    phaserReady: false,
    phaserLoaded: false,
    found: false,
    detectionRate: 4,
    image: null,
    forceFound: false,
    isFirst: true,
    patternsURL: {}
  }),
  computed: {
    isFound() { return this.forceFound ? true : this.found },
    markersReady() { return this.phaserLoaded  }
  },
  activated() {

    TweenMax.to(this.$el, 1, { alpha: 1 })
    if(!this.isFirst) {
      this.found = false

      this.clearUp()

			navigator.mediaDevices.getUserMedia({ video: { frameRate: 30, facingMode: 'environment' } }).then(stream => {
				this.$source.domElement.srcObject = stream
				this.$source.domElement.play()
			})
    }

    this.animate()
  },
  deactivated() {
    this.isFirst = false
    TweenMax.to(this.$el, 1, { alpha: 0 })
		_.each(this.$stream.getTracks(), track => track.stop())
    cancelAnimationFrame(this.raf)

    this.clearUp()
  },
  watch: {
    found: 'onFound',
    phaserLoaded: 'onFound'
  },
  mounted() {
    this.initTHREE()
    this.initAR()
    this.initPhaser()
  },
  methods: {
    onFound() {
      if(!this.phaserLoaded) return

      const marker = this.$route.params.marker
      if(marker == 'singer') return

      if(this.found) {
        this.clearUp()

        this.$store.commit('user/SET_MARKER', marker)
        this.$ghost.visible = true

        this['$sprite_' + marker].visible = true
        this['$sprite_' + marker].anims.play(marker, false, 1)
        this.syncGhostAndStage()
      } else {
        this.clearUp()
      }
    },
    clearUp() {
      this.$ghost.visible = false

      this.$sprite_mrfleur.visible = false
      this.$sprite_trone.visible = false
      this.$sprite_card.visible = false
      this.$sprite_crown.visible = false

      if(this.$syncGhost) this.$syncGhost.kill()
    },
    syncGhostAndStage() {
      if(!this.$stage || !this.$ghost) return

      const stage = this.$stage

      const tl = new TimelineMax({ onComplete: () => this.syncGhostAndStage() })
      tl.add([
        TweenMax.to(this.$ghost.position, 1 / this.detectionRate, {
          x: () => stage.position.x,
          y: () => stage.position.y,
          z: () => stage.position.z,
          ease: Linear.easeInOut
        }),
        TweenMax.to(this.$ghost.scale, 1 / this.detectionRate, {
          x: () => stage.scale.x,
          y: () => stage.scale.y,
          z: () => stage.scale.z,
          ease: Linear.easeInOut
        }),
        TweenMax.to(this.$ghost.quaternion, 1 / this.detectionRate, {
          x: () => stage.quaternion.x,
          y: () => stage.quaternion.y,
          z: () => stage.quaternion.z,
          w: () => stage.quaternion.w,
          ease: Linear.easeInOut
        })
      ])

      this.$syncGhost = tl
    },
    initPhaser() {
      const _this = this
      this.$game = new Phaser.Game({
        type: 1,
        width: 512,
        height: 512,
        canvas: this.$refs.spritesheet_canvas,
        render: { transparent: true },
        scene: {
          preload() {
            this.load.multiatlas('mrfleur', '/dist/mrfleur/mrfleur.json', '/dist/mrfleur')
            this.load.multiatlas('trone', '/dist/trone/trone.json', '/dist/trone')
            this.load.multiatlas('card', '/dist/card/card.json', '/dist/card')
            this.load.multiatlas('crown', '/dist/crown/crown.json', '/dist/crown')

            const texture = new CanvasTexture(_this.$refs.spritesheet_canvas)
            texture.minFilter = LinearFilter
            texture.magFilter = LinearFilter
            const material = new MeshBasicMaterial({ map: texture, side: DoubleSide, transparent: true })
            const model = new Mesh(new PlaneGeometry(5, 5), material)
            TweenMax.set(model, { three: { rotationX: -90 } })

            _this.$model_spritesheet = model
            _this.$ghost.add(model)
            _this.$texture = texture

            _this.$sprite_mrfleur = this.add.sprite(250, 250)
            _this.$sprite_trone = this.add.sprite(250, 250)
            _this.$sprite_card = this.add.sprite(250, 250)
            _this.$sprite_crown = this.add.sprite(250, 250)

            _this.$sprite_mrfleur.visible = false
            _this.$sprite_trone.visible = false
            _this.$sprite_card.visible = false
            _this.$sprite_crown.visible = false

            _this.phaserReady = true
          },
          create() {
            _.each(['mrfleur', 'trone', 'card', 'crown'], name => {
              this.anims.create({
                key: name,
                frames: this.anims.generateFrameNames(name),
                frameRate: 30,
                repeat: -1,
                yoyo: true
              })
            })

            _this.phaserLoaded = true
          }
        }
      })
    },
    initTHREE() {
      const renderer	= new WebGLRenderer({
        alpha: true,
        antialias: true,
        canvas: this.$refs.canvas,
        preserveDrawingBuffer: true
      })
      const scene = new Scene()
      const camera = new Camera()
      const stage = new Group()
      const ghost = new Group()
      const source = new THREEAR.Source({
        parent: this.$el,
        renderer: renderer,
        camera: camera
      })


      // z-fighting bug ?
      let m = camera.projectionMatrix
      let far = 1000
      let near = 0.1
      m.elements[10] = -(far + near) / (far - near)
      m.elements[14] = -(2 * far * near) / (far - near)
      // end z-fighting bug

      renderer.setClearColor(new Color('lightgrey'), 0)
      renderer.setSize(window.innerWidth, window.innerHeight)
      renderer.setPixelRatio(window.devicePixelRatio)

      // renderer.gammaFactor = 2.2
      // renderer.gammaOutput = true

      // light
      scene.add(new PointLight(0xffffff, 1, 2))
      scene.add(new AmbientLight(0xFFFFFFF, 100))
      // end light

      scene.add(ghost)
      scene.add(stage)
      scene.add(camera)

      this.$source = source
      this.$renderer = renderer
      this.$scene = scene
      this.$camera = camera
      this.$stage = stage
      this.$ghost = ghost
			this.$clock = new Clock()
    },
    initAR: async function() {
      const controller = await THREEAR.initialize({
        lostTimeout: 1000 / this.detectionRate,
        source: this.$source,
        maxDetectionRate: this.detectionRate,
        patternRatio: 0.7,
      })

      this.$stream = this.$source.domElement.srcObject

      _.each(this.$root.markers, name => {
        this.patternsURL[name] = require('./markers/' + name + '/marker.patt')
        const patternMarker = new THREEAR.PatternMarker({
          patternUrl: this.patternsURL[name],
          markerObject: this.$stage,
          name: name,
        })
        controller.trackMarker(patternMarker)
      })

      controller.addEventListener('markerFound', (event) => {
        const currentPatternUrl = this.patternsURL[this.$route.params.marker]
        const f = _.find(event.target.markers.pattern, p => p.found && p.patternUrl == currentPatternUrl)
        if(!f) return

        this.found = true
      })

      controller.addEventListener('markerLost', (event) => {
        const currentPatternUrl = this.patternsURL[this.$route.params.marker]
        if(_.find(event.target.markers.pattern, p => !p.found && p.patternUrl == currentPatternUrl))
          this.found = false
      })

      this.$controller = controller

			this.ready = true
    },
    animate() {
      this.raf = requestAnimationFrame(this.animate)

			if(!this.ready) return

      // const delta = this.$clock.getDelta()
      this.$controller.update(this.$source.domElement)

      if(this.$texture && this.found)
        this.$texture.needsUpdate = true

      // if(this.$refs.marker && typeof this.$refs.marker.animate == 'function')
      //   this.$refs.marker.animate(delta)

      this.$renderer.render(this.$scene, this.$camera)
    }
  }
}
</script>

<style lang="stylus">
@import '~@/config/Settings'

.Marker
  height 100%
  text-align center

  &__spritesheet_canvas
    position absolute
    left 0px
    right 0px
    z-index 99
    display none

  // video
  //   opacity 0

  &__canvas
    width 100%
    height 100%
    position absolute
    top 0
    left 0

</style>
